import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAccountBalanceHistory } from '../../api/endpointsBackend';
import AccountDetailPanel from '../accounts/AccountDetailPanel';
import PhoneSheet from './PhoneSheet';

/**
 * One account over any phone page: the account detail panel in a full-height
 * sheet, read-only (no `onAccountUpdated`, so no Edit). The sheet draws the
 * title and the close, as every phone sheet does; the panel leaves out its own
 * header on the phone.
 *
 * `currency` is the balance the sheet opens on: a Home card passes its own,
 * so the headline repeats the figure that was tapped.
 *
 * `buckets` is the balance history the chart draws. The Accounts page already
 * holds it for every account and passes it; a caller without it (Home's
 * balance cards) leaves it out and the sheet loads this account's alone.
 * `reconciliation` is the account's balance-vs-bank status, from a list
 * fetched with it; without it the sheet loads the account's own.
 */
export default function PhoneAccountSheet({
  accountId, isOpen, onClose, reconciliation, buckets, currency, roi,
}) {
  const { t } = useTranslation('accounts');
  const [ownBuckets, setOwnBuckets] = useState(null);

  useEffect(() => {
    if (!isOpen || accountId == null || buckets) return undefined;
    let active = true;
    setOwnBuckets(null);
    const now = new Date();
    // The balance chart reads the account's whole history, as the Accounts
    // page loads it: no start date, so every movement counts.
    getAccountBalanceHistory(null, `${now.getFullYear()}-12-31`, null, { accountIds: [accountId] })
      .then((rows) => { if (active) setOwnBuckets(rows || []); })
      .catch(() => { if (active) setOwnBuckets([]); });
    return () => { active = false; };
  }, [isOpen, accountId, buckets]);

  return (
    <PhoneSheet id="account" isOpen={isOpen} onClose={onClose} size="full" title={t('detail.header')}>
      {accountId != null && (
        <AccountDetailPanel
          accountId={accountId}
          onClose={onClose}
          currency={currency}
          buckets={buckets || ownBuckets || []}
          roi={roi}
          reconciliation={reconciliation}
        />
      )}
    </PhoneSheet>
  );
}

/**
 * The account sheet as a page uses it:
 *
 *   const { openAccount, accountSheet } = usePhoneAccountSheet();
 *   <Card onClick={() => openAccount(account.id)} />
 *   {accountSheet}
 *
 * `openAccount(accountId, { reconciliation, buckets, currency, roi })`; every
 * option may be left out. The last account stays mounted while the sheet
 * slides away, so its content does not blank mid-animation.
 */
export function usePhoneAccountSheet() {
  const [state, setState] = useState({ accountId: null, isOpen: false, options: {} });

  const openAccount = useCallback((accountId, options = {}) => {
    setState({ accountId, isOpen: true, options });
  }, []);
  const closeAccount = useCallback(() => setState((s) => ({ ...s, isOpen: false })), []);

  const accountSheet = (
    <PhoneAccountSheet
      accountId={state.accountId}
      isOpen={state.isOpen}
      onClose={closeAccount}
      {...state.options}
    />
  );

  return { openAccount, closeAccount, accountSheet };
}
